import type { ReactNode } from 'react';
import React from 'react';

import { compose, composeU } from '../lib/compose';
import { Palettes } from '../PaletteGeneration/PaletteGeneration.types';
import { Popup } from '../Popup';
import { withSizeL } from './_size/Tooltip_size_l';
import { withSizeS } from './_size/Tooltip_size_s';
import { TooltipBackdrop as Backdrop } from './Backdrop/Tooltip-Backdrop';
import { cnTooltip } from './Tooltip.cn';
import type { TooltipBaseProps } from './Tooltip.types';

import './Tooltip.css';

export type { Direction } from '../Popup';

const TAIL_OFFSET = 8;

export const enhance = compose(composeU(withSizeS, withSizeL));

/** Компонент используется для создания всплывающих подсказок */
export const Tooltip = enhance((props: TooltipBaseProps) => {
    const {
        anchor,
        boundary,
        children,
        className,
        direction,
        hasTail,
        id,
        innerRef,
        keepMounted,
        mainOffset,
        modifiers,
        disableOverlay,
        onClick,
        onClose,
        scope,
        size = 'm',
        secondaryOffset,
        state,
        tailOffset,
        testId,
        trimText = true,
        viewportOffset,
        visible,
        zIndex,
    } = props;
    const defaultMainOffset = hasTail ? 0 : 4;
    const testIdProp = testId ? { 'data-testid': testId } : {};

    return (
        <Popup
            anchor={anchor}
            boundary={boundary}
            className={cnTooltip({ size, visible, state, text_fixed: trimText }, [
                Palettes.inverse,
                className,
            ])}
            {...testIdProp}
            direction={direction}
            disableOverlay={disableOverlay}
            hasTail={hasTail}
            innerRef={innerRef}
            keepMounted={keepMounted}
            mainOffset={mainOffset || defaultMainOffset}
            modifiers={modifiers}
            onClick={onClick}
            onClose={onClose}
            scope={scope}
            secondaryOffset={secondaryOffset}
            tailOffset={tailOffset}
            target="anchor"
            /* eslint-disable-next-line react/no-unstable-nested-components */
            unstable_onRenderTail={React.useCallback(
                (tail: ReactNode) => (
                    <Backdrop>{hasTail && tail}</Backdrop>
                ),
                [hasTail],
            )}
            viewportOffset={viewportOffset}
            visible={visible}
            zIndex={zIndex}
            arrowMarginThreshold={TAIL_OFFSET}
        >
            <div role="tooltip" id={id} className={cnTooltip('Content')}>
                {children}
            </div>
        </Popup>
    );
});

Tooltip.displayName = cnTooltip();

export type TooltipProps = React.ComponentProps<typeof Tooltip>;
